/**
 * Alluvion Home — Live Frame Preview
 * ====================================
 * Listens to Dawn's native dropdowns and composites the customer's
 * photo into the correct frame mockup image.
 *
 * Only runs on: /products/print-your-own-photo
 *
 * INSTALLATION
 * ─────────────────────────────────────────
 * STEP 1 — Add this file as a theme asset
 *   Shopify Admin → Online Store → Themes → Edit code
 *   Assets → Add a new asset → name it alluvion-preview.js → paste this file
 *
 * STEP 2 — Load BEFORE alluvion-configurator.js in layout/theme.liquid:
 *
 *   {% if request.path contains '/products/print-your-own-photo' %}
 *     <script src="{{ 'alluvion-preview.js' | asset_url }}" defer></script>
 *     <script src="{{ 'alluvion-configurator.js' | asset_url }}" defer></script>
 *   {% endif %}
 */

(function () {
  'use strict';

  if (!window.location.pathname.includes('/products/print-your-own-photo')) return;

  /* ── Framing value → mockup key ──────────────────────────────────── */
  var STYLE_KEY = {
    'Print Only':                  'print-only-no-passepartout',
    'Print Only With Mount':       'print-only-passepartout',
    'Black Frame':                 'black-frame-no-passepartout',
    'Black Frame With Mount':      'black-frame-passepartout',
    'White Frame':                 'white-frame-no-passepartout',
    'White Frame With Mount':      'white-frame-passepartout',
    'Light Oak Frame':             'light-oak-frame-no-passepartout',
    'Light Oak Frame With Mount':  'light-oak-frame-passepartout',
    'Dark Oak Frame':              'dark-oak-frame-no-passepartout',
    'Dark Oak Frame With Mount':   'dark-oak-frame-passepartout',
  };

  /* ── Artwork window coordinates (% of mockup image size) ─────────── */
  /* Portrait */
  var WINDOWS = {
    'black-frame-no-passepartout-portrait':      { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'black-frame-passepartout-portrait':         { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'white-frame-no-passepartout-portrait':      { left: 30.0, top: 14.0, width: 39.9, height: 69.7 },
    'white-frame-passepartout-portrait':         { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'dark-oak-frame-no-passepartout-portrait':   { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'dark-oak-frame-passepartout-portrait':      { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'light-oak-frame-no-passepartout-portrait':  { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'light-oak-frame-passepartout-portrait':     { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'print-only-no-passepartout-portrait':       { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'print-only-passepartout-portrait':          { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    /* Landscape */
    'black-frame-no-passepartout-landscape':     { left: 23.1, top: 26.2, width: 55.8, height: 49.9 },
    'black-frame-passepartout-landscape':        { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'white-frame-no-passepartout-landscape':     { left: 23.1, top: 26.2, width: 55.8, height: 49.9 },
    'white-frame-passepartout-landscape':        { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'dark-oak-frame-no-passepartout-landscape':  { left: 23.2, top: 25.9, width: 55.8, height: 49.9 },
    'dark-oak-frame-passepartout-landscape':     { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'light-oak-frame-no-passepartout-landscape': { left: 23.2, top: 26.2, width: 55.9, height: 49.9 },
    'light-oak-frame-passepartout-landscape':    { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'print-only-no-passepartout-landscape':      { left: 22.3, top: 14.1, width: 55.8, height: 49.8 },
    'print-only-passepartout-landscape':         { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
  };

  /* ── State ────────────────────────────────────────────────────────── */
  var currentFraming     = null;
  var currentOrientation = 'portrait';
  var photoDataUrl       = null;
  var productImages      = {};

  /* ── Boot ─────────────────────────────────────────────────────────── */
  function init() {
    collectProductImages();
    injectPreviewStyles();
    replaceGallery();
    bindDawnDropdowns();
    bindConfiguratorEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Collect product image URLs by alt text ──────────────────────── */
  function collectProductImages() {
    var imgs = document.querySelectorAll(
      '.product__media img, .product-single__media img, [data-media-type="image"] img, .product__media-list img'
    );
    imgs.forEach(function (img) {
      var alt = (img.alt || '').trim().toLowerCase().replace(/\s+/g, '-');
      var src = img.src || img.dataset.src || '';
      if (alt && src && WINDOWS[alt]) {
        src = src.replace(/(_\d+x\d*|_\d*x\d+)(\.\w+)(\?|$)/, '$2$3');
        productImages[alt] = src;
      }
      /* Also try srcset for highest res */
      var srcset = img.srcset || img.dataset.srcset || '';
      if (srcset && alt && WINDOWS[alt]) {
        var parts = srcset.split(',').map(function (s) { return s.trim().split(/\s+/); });
        var largest = parts.reduce(function (best, curr) {
          var w = parseInt((curr[1] || '0').replace('w', ''), 10);
          return w > best.w ? { url: curr[0], w: w } : best;
        }, { url: '', w: 0 });
        if (largest.url) productImages[alt] = largest.url;
      }
    });
    console.log('[Alluvion Preview] Found ' + Object.keys(productImages).length + ' mockup images');
  }

  /* ── Build mockup key from current state ─────────────────────────── */
  function getMockupKey() {
    var styleBase = currentFraming ? (STYLE_KEY[currentFraming] || 'print-only-no-passepartout') : 'print-only-no-passepartout';
    return styleBase + '-' + currentOrientation;
  }

  /* ── Replace Dawn's gallery with our preview ─────────────────────── */
  function replaceGallery() {
    var gallery = document.querySelector('.product__media-wrapper');
    if (!gallery) {
      console.warn('[Alluvion Preview] Could not find product gallery');
      return;
    }

    /* Hide Dawn's media gallery but keep the wrapper for layout */
    var mediaGallery = gallery.querySelector('media-gallery');
    if (mediaGallery) mediaGallery.style.display = 'none';

    /* Build preview container */
    var container = document.createElement('div');
    container.id = 'alv-preview-container';
    container.innerHTML =
      '<div id="alv-preview-frame">' +
        '<img id="alv-preview-mockup" src="" alt="Frame preview" style="display:none;">' +
        '<div id="alv-preview-photo-wrap" style="display:none;">' +
          '<img id="alv-preview-photo" src="" alt="Your photo preview">' +
        '</div>' +
        '<div id="alv-preview-placeholder">' +
          '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>' +
          '<p>Upload your photo to see a preview</p>' +
        '</div>' +
      '</div>';

    gallery.appendChild(container);
    renderPreview();
  }

  /* ── Render the preview ──────────────────────────────────────────── */
  function renderPreview() {
    var mockupImg   = document.getElementById('alv-preview-mockup');
    var photoWrap   = document.getElementById('alv-preview-photo-wrap');
    var photoImg    = document.getElementById('alv-preview-photo');
    var placeholder = document.getElementById('alv-preview-placeholder');
    if (!mockupImg) return;

    var key = getMockupKey();
    var win = WINDOWS[key];
    var src = productImages[key];

    /* Fallback to portrait print-only if no match */
    if (!src) {
      key = 'print-only-no-passepartout-portrait';
      win = WINDOWS[key];
      src = productImages[key];
    }

    if (src) {
      mockupImg.src          = src;
      mockupImg.style.display = 'block';
    }

    if (win && photoDataUrl) {
      photoWrap.style.display  = 'block';
      photoWrap.style.left     = win.left + '%';
      photoWrap.style.top      = win.top + '%';
      photoWrap.style.width    = win.width + '%';
      photoWrap.style.height   = win.height + '%';
      photoImg.src             = photoDataUrl;
      placeholder.style.display = 'none';
    } else {
      photoWrap.style.display   = 'none';
      placeholder.style.display = src ? 'flex' : 'none';
    }
  }

  /* ── Listen to Dawn's native dropdowns ───────────────────────────── */
  function bindDawnDropdowns() {
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || !el.name) return;

      if (el.name.indexOf('Framing') !== -1) {
        currentFraming = el.value;
        renderPreview();
      }
    });
  }

  /* ── Listen for events from alluvion-configurator.js ────────────── */
  function bindConfiguratorEvents() {
    document.addEventListener('alv:photo-changed', function (e) {
      photoDataUrl       = e.detail.dataUrl;
      currentOrientation = e.detail.orientation || 'portrait';
      renderPreview();
    });

    document.addEventListener('alv:photo-cleared', function () {
      photoDataUrl       = null;
      currentOrientation = 'portrait';
      renderPreview();
    });
  }

  /* ── Inject preview CSS ──────────────────────────────────────────── */
  function injectPreviewStyles() {
    if (document.getElementById('alv-preview-styles')) return;
    var style = document.createElement('style');
    style.id  = 'alv-preview-styles';
    style.textContent = [
      '#alv-preview-container { width: 100%; }',
      '#alv-preview-frame { position: relative; width: 100%; line-height: 0; border-radius: var(--media-radius,4px); overflow: hidden; background: rgb(var(--color-background)); }',
      '#alv-preview-mockup { width: 100%; height: auto; display: block; }',
      '#alv-preview-photo-wrap { position: absolute; overflow: hidden; }',
      '#alv-preview-photo { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }',
      '#alv-preview-placeholder { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .8rem; pointer-events: none; }',
      '#alv-preview-placeholder p { font-size: 1.2rem; color: rgba(var(--color-foreground),.4); margin: 0; font-family: var(--font-body-family); }',
    ].join('\n');
    document.head.appendChild(style);
  }

})();